import { useEffect, useRef, useState } from "react";
import {
  adminCoReadDelete,
  adminCoReadGet,
  adminCoReadPost,
  adminCoReadUpload,
} from "../../api/coReadApi";

export default function AdminCoRead() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [chapterForm, setChapterForm] = useState({
    chapter_id: null,
    title: "",
    body_text: "",
    sort_order: 0,
  });
  const [threads, setThreads] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await adminCoReadGet("/admin/overview");
      setItems(data.items || []);
      if (!selectedId && data.items?.[0]) {
        setSelectedId(data.items[0].id);
      }
    } catch (e) {
      setMessage(e.message || "加载失败");
    }
  };

  const loadChapters = async (readingId) => {
    if (!readingId) return;
    try {
      const data = await adminCoReadGet(`/chapters?reading_id=${readingId}`);
      setChapters(data.items || []);
    } catch {
      setChapters([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) {
      loadChapters(selectedId);
      loadSummaries(selectedId);
      loadThreads(selectedId);
    }
  }, [selectedId]);

  const generateCommunitySummary = async () => {
    if (!selectedId) return;
    setSummaryBusy(true);
    setMessage("");
    try {
      const data = await adminCoReadPost("/admin/summaries", {
        reading_id: selectedId,
        period_type: summaryPeriod,
      });
      setMessage(data.message || "草稿已生成");
      if (data.item?.id && data.item?.output_text) {
        setEditDraft((prev) => ({ ...prev, [data.item.id]: data.item.output_text }));
      }
      await loadSummaries(selectedId);
    } catch (e) {
      setMessage(e.message || "生成失败");
    } finally {
      setSummaryBusy(false);
    }
  };

  const publishSummary = async (id, publish = true) => {
    try {
      const body = { publish };
      if (editDraft[id] != null) body.output_text = editDraft[id];
      const data = await adminCoReadPost(`/admin/summaries/${id}/publish`, body);
      setMessage(data.message || (publish ? "已发布" : "已取消发布"));
      await loadSummaries(selectedId);
    } catch (e) {
      setMessage(e.message || "操作失败");
    }
  };

  const selected = items.find((i) => i.id === selectedId);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedId) return;
    setUploading(true);
    setMessage("");
    try {
      const data = await adminCoReadUpload(selectedId, file);
      setMessage(data.message || "上传成功");
      await load();
    } catch (err) {
      setMessage(err.message || "上传失败");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const [groupSize, setGroupSize] = useState(1);
  const [summaryPeriod, setSummaryPeriod] = useState("week");
  const [summaries, setSummaries] = useState([]);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [editDraft, setEditDraft] = useState({});

  const loadSummaries = async (readingId) => {
    if (!readingId) return;
    try {
      const data = await adminCoReadGet(`/admin/summaries?reading_id=${readingId}`);
      setSummaries(data.items || []);
    } catch {
      setSummaries([]);
    }
  };

  const loadThreads = async (readingId) => {
    if (!readingId) return;
    try {
      const data = await adminCoReadGet(`/admin/threads?reading_id=${readingId}`);
      setThreads(data.items || []);
    } catch {
      setThreads([]);
    }
  };

  const autoSplit = async (mode = "smart") => {
    if (!selectedId) return;
    const g = Number(groupSize) || 1;
    let tip =
      "将智能拆分（优先认 I/II/IV 等信件编号或章标题），会覆盖现有章节，是否继续？";
    if (mode === "chunks") {
      tip = "将按约 6000 字一段重建章节（不依赖标题），是否继续？";
    } else if (mode === "letters") {
      tip =
        g > 1
          ? `将按罗马数字信件编号拆分，并每 ${g} 封合并为一段，是否继续？`
          : "将按罗马数字信件编号（I、II、III、IV…）每封一章拆分，是否继续？";
    }
    if (!window.confirm(tip)) return;
    try {
      const q = new URLSearchParams({
        reading_id: String(selectedId),
        mode,
        chunk_size: "6000",
        group_size: String(g),
      });
      const data = await adminCoReadPost(`/admin/auto-split?${q}`, {});
      setMessage(data.message || "已拆分");
      await loadChapters(selectedId);
      await load();
    } catch (e) {
      setMessage(e.message || "拆分失败");
    }
  };

  const saveChapter = async () => {
    if (!selectedId || !chapterForm.title.trim()) {
      alert("请填写章节标题");
      return;
    }
    try {
      await adminCoReadPost("/admin/chapters", {
        reading_id: selectedId,
        chapter_id: chapterForm.chapter_id || null,
        title: chapterForm.title,
        body_text: chapterForm.body_text,
        sort_order: Number(chapterForm.sort_order) || 0,
        is_enabled: 1,
      });
      setChapterForm({
        chapter_id: null,
        title: "",
        body_text: "",
        sort_order: chapters.length + 1,
      });
      setMessage(chapterForm.chapter_id ? "章节已更新" : "章节已保存");
      await loadChapters(selectedId);
      await load();
    } catch (e) {
      setMessage(e.message || "保存失败");
    }
  };

  const editChapter = async (id) => {
    try {
      const data = await adminCoReadGet(`/admin/chapters/${id}`);
      const c = data.item;
      setChapterForm({
        chapter_id: c.id,
        title: c.title || "",
        body_text: c.body_text || "",
        sort_order: c.sort_order || 0,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      setMessage(`正在编辑：${c.title}`);
    } catch (e) {
      setMessage(e.message || "加载章节失败");
    }
  };

  const moderateThread = async (id, patch) => {
    try {
      await adminCoReadPost(`/admin/threads/${id}`, patch);
      setMessage("讨论已更新");
      await loadThreads(selectedId);
    } catch (e) {
      setMessage(e.message || "操作失败");
    }
  };

  const setCurrent = async (chapterId) => {
    try {
      await adminCoReadPost("/admin/current-chapter", {
        reading_id: selectedId,
        chapter_id: chapterId,
      });
      setMessage("已设为当前共读章节");
      await load();
    } catch (e) {
      setMessage(e.message || "更新失败");
    }
  };

  const removeChapter = async (id) => {
    if (!window.confirm("删除该章节？")) return;
    try {
      await adminCoReadDelete(`/admin/chapters/${id}`);
      await loadChapters(selectedId);
      await load();
    } catch (e) {
      setMessage(e.message || "删除失败");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">AI 共读 · 电子档与章节</h2>
        <p className="text-sm text-gray-500 mt-1">
          为读书会「期次书目」上传电子档。推荐 TXT / EPUB（最稳）；PDF 需是「可选中复制文字」的版本，扫描版图片 PDF
          无法抽取文字。提取成功后成员即可在「AI 共读中心」阅读与提炼。
        </p>
      </div>

      {message && (
        <div className="text-sm bg-blue-50 text-blue-700 px-4 py-2 rounded-xl">{message}</div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border p-4 space-y-2">
          <h3 className="font-semibold text-gray-800">期次书目</h3>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm ${
                selectedId === item.id ? "bg-pink-500 text-white" : "hover:bg-pink-50"
              }`}
            >
              <div className="font-medium">
                {item.is_current ? "【当期】" : ""}
                {item.book_title}
              </div>
              <div className={`text-xs ${selectedId === item.id ? "text-pink-100" : "text-gray-400"}`}>
                {item.material?.has_file ? "已传电子档" : "未传"} · {item.chapter_count || 0} 章
              </div>
            </button>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-gray-400">请先在「读书会管理」添加期次书目</p>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          {selected ? (
            <>
              <div className="bg-white rounded-2xl border p-5 space-y-3">
                <h3 className="font-semibold text-gray-800">{selected.book_title}</h3>
                <p className="text-sm text-gray-500">
                  {selected.material?.has_file
                    ? `${selected.material.file_name} · ${selected.material.extract_note || selected.material.extract_status}`
                    : "尚未上传电子档"}
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.epub,.txt,.mobi,application/pdf"
                  className="hidden"
                  onChange={onUpload}
                />
                <div className="flex flex-wrap gap-2">
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    若 PDF 报错：优先上传 TXT；或用 Word 打开 PDF 后「另存为纯文本」。服务器需已安装
                    pypdf（pip install pypdf）。
                  </p>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm disabled:opacity-50"
                  >
                    {uploading ? "上传解析中…" : "上传 / 更换电子档"}
                  </button>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <span>每几封信合成一段</span>
                    <select
                      value={groupSize}
                      onChange={(e) => setGroupSize(Number(e.target.value))}
                      className="border rounded-lg px-2 py-1"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} 封
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => autoSplit("letters")}
                    className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm"
                  >
                    按信件编号拆分（I / II / IV…）
                  </button>
                  <button
                    type="button"
                    onClick={() => autoSplit("smart")}
                    className="px-4 py-2 rounded-xl border text-sm"
                  >
                    智能拆分
                  </button>
                  <button
                    type="button"
                    onClick={() => autoSplit("chunks")}
                    className="px-4 py-2 rounded-xl border text-sm"
                  >
                    按篇幅切分（约6000字/段）
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border p-5 space-y-3">
                <h3 className="font-semibold text-gray-800">
                  {chapterForm.chapter_id ? `编辑章节 #${chapterForm.chapter_id}` : "手动添加章节"}
                </h3>
                <input
                  value={chapterForm.title}
                  onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                  placeholder="章节标题"
                  className="w-full border rounded-xl px-3 py-2 text-sm"
                />
                <textarea
                  value={chapterForm.body_text}
                  onChange={(e) =>
                    setChapterForm({ ...chapterForm, body_text: e.target.value })
                  }
                  placeholder="章节正文（可粘贴；单章提炼依赖此内容）"
                  rows={8}
                  className="w-full border rounded-xl px-3 py-2 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveChapter}
                    className="px-4 py-2 rounded-xl bg-gray-800 text-white text-sm"
                  >
                    {chapterForm.chapter_id ? "更新章节" : "保存章节"}
                  </button>
                  {chapterForm.chapter_id ? (
                    <button
                      type="button"
                      onClick={() =>
                        setChapterForm({
                          chapter_id: null,
                          title: "",
                          body_text: "",
                          sort_order: 0,
                        })
                      }
                      className="px-4 py-2 rounded-xl border text-sm"
                    >
                      取消编辑
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="bg-white rounded-2xl border p-5 space-y-2">
                <h3 className="font-semibold text-gray-800">章节列表（{chapters.length}）</h3>
                {chapters.map((c) => (
                  <div
                    key={c.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-50 py-2 text-sm"
                  >
                    <div>
                      <span className="font-medium">{c.title}</span>
                      <span className="text-gray-400 ml-2">
                        {c.has_body ? `${c.char_count} 字` : "无正文"}
                        {selected.material?.current_chapter_id === c.id ? " · 当前共读" : ""}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-amber-700"
                        onClick={() => editChapter(c.id)}
                      >
                        编辑
                      </button>
                      <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => setCurrent(c.id)}
                      >
                        设为当前
                      </button>
                      <button
                        type="button"
                        className="text-red-500"
                        onClick={() => removeChapter(c.id)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl border p-5 space-y-3">
                <h3 className="font-semibold text-gray-800">周 / 月团体资料包（P4）</h3>
                <p className="text-xs text-gray-500">
                  基于公开讨论与章节活动生成草稿，审改后发布给成员；不包含私人笔记。
                </p>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={summaryPeriod}
                    onChange={(e) => setSummaryPeriod(e.target.value)}
                    className="border rounded-xl px-3 py-2 text-sm"
                  >
                    <option value="week">本周总结</option>
                    <option value="month">本月线下资料包</option>
                  </select>
                  <button
                    type="button"
                    disabled={summaryBusy}
                    onClick={generateCommunitySummary}
                    className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm disabled:opacity-50"
                  >
                    {summaryBusy ? "生成中…" : "生成草稿"}
                  </button>
                </div>
                {summaries.length === 0 ? (
                  <p className="text-sm text-gray-400">暂无草稿</p>
                ) : (
                  summaries.map((s) => (
                    <div key={s.id} className="border rounded-xl p-3 space-y-2 text-sm">
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-medium">
                          {s.title}{" "}
                          <span className="text-gray-400 font-normal">
                            {s.is_published ? "· 已发布" : "· 草稿"}
                          </span>
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="text-blue-600"
                            onClick={() => publishSummary(s.id, true)}
                          >
                            保存并发布
                          </button>
                          {s.is_published ? (
                            <button
                              type="button"
                              className="text-gray-500"
                              onClick={() => publishSummary(s.id, false)}
                            >
                              取消发布
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <textarea
                        value={editDraft[s.id] != null ? editDraft[s.id] : s.output_text || ""}
                        onChange={(e) =>
                          setEditDraft((prev) => ({ ...prev, [s.id]: e.target.value }))
                        }
                        rows={8}
                        className="w-full border rounded-xl px-3 py-2 text-sm font-sans"
                      />
                      <button
                        type="button"
                        className="text-xs text-amber-700"
                        onClick={() => {
                          const text =
                            editDraft[s.id] != null ? editDraft[s.id] : s.output_text || "";
                          const blob = new Blob([text], {
                            type: "text/plain;charset=utf-8",
                          });
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(blob);
                          a.download = `${s.period_key || "pack"}-${s.period_type}.txt`;
                          a.click();
                          URL.revokeObjectURL(a.href);
                        }}
                      >
                        导出 TXT
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="bg-white rounded-2xl border p-5 space-y-3">
                <h3 className="font-semibold text-gray-800">讨论管理</h3>
                <p className="text-xs text-gray-500">置顶可作为「今日讨论问题」；隐藏后成员不可见。</p>
                {threads.length === 0 ? (
                  <p className="text-sm text-gray-400">暂无讨论</p>
                ) : (
                  threads.map((t) => (
                    <div
                      key={t.id}
                      className="border rounded-xl p-3 text-sm flex flex-wrap justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">
                          {t.is_pinned ? "📌 " : ""}
                          {t.is_hidden ? "（已隐藏） " : ""}
                          {t.title || t.content}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t.author_name} · 赞 {t.like_count || 0} · 回复 {t.reply_count || 0}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          type="button"
                          className="text-blue-600"
                          onClick={() =>
                            moderateThread(t.id, { is_pinned: t.is_pinned ? 0 : 1 })
                          }
                        >
                          {t.is_pinned ? "取消置顶" : "置顶"}
                        </button>
                        <button
                          type="button"
                          className="text-red-500"
                          onClick={() =>
                            moderateThread(t.id, { is_hidden: t.is_hidden ? 0 : 1 })
                          }
                        >
                          {t.is_hidden ? "取消隐藏" : "隐藏"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl border p-10 text-center text-gray-400">
              请选择左侧期次
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
